import { useEffect, useState } from "react";
import {
  CalendarIcon,
  PhoneIcon,
  EnvelopeIcon,
  ClipboardDocumentIcon,
  UserIcon,
  CheckIcon,
} from "@heroicons/react/24/outline";
import axios from "axios";

const iconByType = {
  Appel: <PhoneIcon className="w-5 h-5 text-blue-500" />,
  Email: <EnvelopeIcon className="w-5 h-5 text-yellow-500" />,
  "Rendez-vous": <CalendarIcon className="w-5 h-5 text-green-500" />,
  "Message LinkedIn": <EnvelopeIcon className="w-5 h-5 text-indigo-500" />,
  "Note interne": <ClipboardDocumentIcon className="w-5 h-5 text-gray-500" />,
  Autre: <UserIcon className="w-5 h-5 text-gray-400" />,
};

const suggestionsClient = [
  { type: "Note interne", note: "Vérifier la conformité des documents" },
  { type: "Email", note: "Envoyer rapport trimestriel" },
  { type: "Appel", note: "Faire un point régulier" },
];

export default function FamilleStatus({ famille }) {
  const [actions, setActions] = useState([]);
  const BACKEND_URL =
    window.location.hostname === "localhost"
      ? process.env.REACT_APP_BACKEND_URL_LOCAL + "/api"
      : process.env.REACT_APP_BACKEND_URL_PROD + "/api";

  useEffect(() => {
    const fetchActions = async () => {
      if (!famille?._id) {
        // Suggestion générique si aucune famille
        setActions(suggestionsClient.map((sugg) => ({
          icon: iconByType[sugg.type] || iconByType.Autre,
          titre: sugg.note,
          type: sugg.type,
          canal: "N/A",
          date: null,
          source: "suggestion",
        })));
        return;
      }

      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`${BACKEND_URL}/interactions/famille/${famille._id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const allInteractions = res.data;

        const futures = allInteractions
          .filter((i) => i.prochaineEtape || i.dateProchaineInteraction)
          .sort((a, b) => new Date(a.dateProchaineInteraction) - new Date(b.dateProchaineInteraction))
          .map((item) => ({
            icon: iconByType[item.typeInteraction] || iconByType.Autre,
            titre: item.prochaineEtape || "Étape à préciser",
            date: item.dateProchaineInteraction
              ? new Date(item.dateProchaineInteraction).toLocaleDateString()
              : null,
            canal: item.canal,
            type: item.typeInteraction,
            source: "futur",
          }));

        const suggestionsDéjàRéalisées = new Set(
          allInteractions.map((i) => `${i.typeInteraction}_${i.contenu?.trim()}`)
        );

        const suggestions = suggestionsClient
          .filter((sugg) => !suggestionsDéjàRéalisées.has(`${sugg.type}_${sugg.note.trim()}`))
          .map((sugg) => ({
            icon: iconByType[sugg.type] || iconByType.Autre,
            titre: sugg.note,
            type: sugg.type,
            canal: "N/A",
            date: null,
            source: "suggestion",
          }));

        setActions([...futures, ...suggestions]);
      } catch (err) {
        console.error("Erreur récupération interactions famille :", err);
        setActions([]);
      }
    };

    fetchActions();
  }, [famille]);

  const handleMarkAsDone = async (action) => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${BACKEND_URL}/interactions`, {
        typeInteraction: action.type,
        canal: action.canal === "N/A" ? "Autre" : action.canal,
        date: new Date(),
        contenu: action.titre,
        statut: famille?.statut || "Client",
        referent: famille?.referent || "Non assigné",
        prospectId: famille?._id || null,
        commentaireInterne: "Ajouté depuis FamilleStatus sans sélection",
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });

      alert("Interaction enregistrée !");
    } catch (err) {
      console.error("Erreur ajout interaction famille :", err);
    }
  };

  return (
    <div className="bg-white shadow-md rounded-lg p-6 w-[400px] h-[300px] flex flex-col">
      <div className="flex flex-col items-center justify-center pb-4">
        <h2 className="text-lg font-semibold mb-2">À Faire</h2>

        {actions.length > 0 ? (
          <div className="space-y-3 w-full">
            {actions.slice(0, 2).map((action, index) => (
              <div
                key={index}
                className="flex flex-col gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200"
              >
                <div className="flex items-start gap-3">
                  <div className="mt-1">{action.icon}</div>
                  <div className="flex-1">
                    <p className="font-semibold text-gray-700">
                      {action.titre} {action.date && `– ${action.date}`}
                    </p>
                    <p className="text-sm text-gray-600 italic">
                      ({action.type}{action.canal !== "N/A" && ` via ${action.canal}`})
                    </p>
                  </div>
                </div>
                <button
                  className="flex items-center justify-center gap-2 self-end text-sm text-green-600 hover:text-green-800"
                  onClick={() => handleMarkAsDone(action)}
                >
                  <CheckIcon className="w-4 h-4" />
                  Marquer comme faite
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500">Aucune action planifiée</p>
        )}
      </div>
    </div>
  );
}
